import { useEffect, useRef } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { Me } from "@jaks/contract";
import { mobileTabsFor, tabLabelOf } from "@jaks/contract/registry";
import { api } from "@jaks/core/api-client";
import { Empty } from "@jaks/ui";
import { MODULES, moduleOf, visibleGroups } from "../manifest.ts";
import { Icon } from "./icons.tsx";
import { Bell } from "./bell.tsx";
import { QuickLinks } from "./quick-links.tsx";
import logoUrl from "../assets/wordmark-white.png";
import { VERSION_LABEL, VERSION_TITLE } from "./version.ts";

export function useMe() {
  return useQuery<Me>({ queryKey: ["me"], queryFn: () => api<Me>("/api/me"), staleTime: 5 * 60_000, retry: false });
}

/** `/jobs` and `/jobs/anything` are both Jobs; `/` is Home before the redirect lands. */
function sectionAt(path: string): string {
  if (path === "/") return "home";
  return MODULES.find((m) => path === `/${m.id}` || path.startsWith(`/${m.id}/`))?.id ?? "";
}

export function Shell() {
  const me = useMe();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const allowedIds = me.data?.allowedModules;
  const groups = visibleGroups(allowedIds);
  const asked = sectionAt(path);
  const activeId = allowedIds?.includes(asked) ? asked : path === "/more" ? "more" : "";
  const tabs = mobileTabsFor(me.data?.roles, allowedIds);
  // A typed URL is a way in too: the API refuses, but the screen should say so
  // rather than render a page of failed panels.
  const blocked = Boolean(asked && allowedIds && !allowedIds.includes(asked));
  const blockedSection = blocked ? moduleOf(asked) : undefined;

  if (me.isLoading) return <div style={{ display: "grid", placeItems: "center", height: "100%" }} className="muted">Signing you in…</div>;
  // Without /api/me there is no way to know what this person may open, and
  // guessing is how the old shell showed sections that only ever failed.
  //
  // Landing here does NOT mean "not signed in": a 401 never reaches this
  // branch, because the api client bounces it straight to /api/auth/login.
  // This is /api/me answering something else — a 500, a cold database, a
  // network that dropped — so the honest thing is to offer both ways out
  // rather than the one that used to be here. "Reload the page" alone left
  // somebody whose session had gone bad with nothing to press.
  if (!me.data) {
    return (
      <div style={{ display: "grid", placeItems: "center", height: "100%", textAlign: "center" }}>
        <div>
          <p className="muted" style={{ margin: "0 0 12px" }}>JAKS could not load your account.</p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            <button type="button" className="btn" onClick={() => me.refetch()}>Try again</button>
            <a className="btn" href={`/api/auth/login?dest=${encodeURIComponent(path)}`}>Sign in</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="shell">
      <a href="#main" className="skip">Skip to content</a>
      <aside className="sidebar">
        <img src={logoUrl} alt="JAKS" className="logo" width={150} />
        {/* Which bundle this browser is running. Always on screen, because the
            failure it exists to catch is somebody reporting a problem against a
            version nobody can identify. */}
        <div className="version" title={VERSION_TITLE}>{VERSION_LABEL}</div>
        <nav aria-label="Sections" onKeyDown={arrowThrough(".nav-a")}>
          {groups.map(({ group, items }) => (
            <div key={group} role="group" aria-labelledby={`nav-g-${group}`} className="nav-group">
              <div className="g" id={`nav-g-${group}`}>{group}</div>
              {items.map((m) => (
                <Link
                  key={m.id}
                  to={`/${m.id}`}
                  className={"nav-a" + (m.id === activeId ? " active" : "")}
                  aria-current={m.id === activeId ? "page" : undefined}
                >
                  <Icon name={m.icon} /> <span>{m.label}</span>
                </Link>
              ))}
            </div>
          ))}
          {!groups.length && (
            <p className="nav-none">
              Nothing is switched on for your account yet. An admin turns sections on in Settings → Permissions.
            </p>
          )}
        </nav>
        <div className="util">
          <QuickLinks onTheme={toggleTheme} />
          <div className="me-row">
            <div className="avatar">{initials(me.data.name)}</div>
            <div className="who"><b>{me.data.name}</b><span>{me.data.email}</span></div>
          </div>
          <div className="me-actions">
            {/*
              Since T15 this is the ONLY way into Settings on a desktop: it left
              navigation, and the Admin heading over its single row left with it.
              The gate is unchanged — it was already the entrance admins used and
              non-admins never saw either of them. The phone's counterpart is a
              row at the bottom of the More sheet, under this same condition.
            */}
            {allowedIds?.includes("settings") && (
              <Link to="/settings" className="me-btn"><Icon name="gear" size={15} /> Settings</Link>
            )}
            <button className="me-btn" onClick={signOut}><Icon name="signout" size={15} /> Sign out</button>
          </div>
        </div>
      </aside>
      <Main path={path}>
        {blocked
          ? <Empty
              icon="🔒"
              title={`${blockedSection?.label ?? "That section"} is not enabled for your role`}
              // A standalone route has no group, and naming one that does not
              // exist read "lives under undefined" — latent since T15 for
              // Settings, and reachable since T33 for Capture.
              body={`${blockedSection?.group ? `${blockedSection.label} lives under ${blockedSection.group} for the people who have it. ` : ""}Ask an admin to turn it on for you in Settings → Permissions.`}
            />
          : <Outlet />}
      </Main>
      <Bell variant="mobile" />
      <nav className="tabbar" aria-label="Primary" onKeyDown={arrowThrough("a")}>
        {tabs.map((m) => {
          const chrome = moduleOf(m.id);
          return (
            <Link
              key={m.id}
              to={`/${m.id}`}
              className={m.id === activeId ? "active" : ""}
              aria-current={m.id === activeId ? "page" : undefined}
            >
              <Icon name={chrome?.icon ?? "open"} size={22} /> <span>{tabLabelOf(m)}</span>
            </Link>
          );
        })}
        <Link to="/more" className={activeId === "more" ? "active" : ""} aria-current={activeId === "more" ? "page" : undefined}>
          <Icon name="more" size={22} /> <span>More</span>
        </Link>
      </nav>
    </div>
  );
}

/**
 * The content region, and the thing a keyboard lands on after a move. Without
 * this, following a sidebar link leaves focus behind in the sidebar, so the
 * next Tab walks the rest of the nav instead of entering the page that just
 * opened.
 *
 * Arriving is not a move: the page you loaded is already the page you asked
 * for, and taking focus off the body would put the skip link out of reach on
 * the very first Tab. So this compares paths rather than counting renders — a
 * "have I run before" flag reads as equivalent and is not, because StrictMode
 * runs mount effects twice and the second run would steal focus on arrival.
 */
function Main({ path, children }: { path: string; children: ReactNode }) {
  const ref = useRef<HTMLElement>(null);
  const seen = useRef(path);
  useEffect(() => {
    if (seen.current === path) return;
    seen.current = path;
    ref.current?.focus();
  }, [path]);
  // A screen that fills the main area rather than stacking inside it. Only
  // /capture, which is one frame; a list of them would want a flag in the
  // manifest instead.
  const fills = path === "/capture";
  return (
    <main id="main" className={fills ? "main main--frame" : "main"} ref={ref} tabIndex={-1}>
      {children}
    </main>
  );
}

/**
 * Arrow keys walk a nav; Home and End jump to its ends. Every link keeps its
 * place in the tab order — a roving tabindex would make Tab skip the whole
 * sidebar, which is worse than the problem it solves for a list of links.
 */
function arrowThrough(selector: string) {
  return (e: KeyboardEvent<HTMLElement>) => {
    const keys = ["ArrowDown", "ArrowUp", "ArrowRight", "ArrowLeft", "Home", "End"];
    if (!keys.includes(e.key)) return;
    const items = [...e.currentTarget.querySelectorAll<HTMLElement>(selector)];
    if (items.length < 2) return;
    const at = items.indexOf(document.activeElement as HTMLElement);
    if (at === -1) return;
    const step = e.key === "ArrowDown" || e.key === "ArrowRight" ? 1 : e.key === "ArrowUp" || e.key === "ArrowLeft" ? -1 : 0;
    const next = e.key === "Home" ? 0 : e.key === "End" ? items.length - 1 : (at + step + items.length) % items.length;
    items[next]?.focus();
    e.preventDefault();
  };
}

function signOut() {
  fetch("/api/auth/logout", { method: "POST", headers: { "x-amz-content-sha256": "e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855" } })
    .finally(() => location.assign("/"));
}

function initials(n: string) { return n.split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase(); }
function toggleTheme() {
  const cur = document.documentElement.getAttribute("data-theme");
  const next = cur === "dark" ? "light" : "dark";
  document.documentElement.setAttribute("data-theme", next);
  localStorage.setItem("jaks-theme", next);
}
